import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiStepForm, Step } from '../../../../components/molecules/Form/components/MultiStepForm';
import { Namespace } from '../../../../translation/namespace';
import { translatePartOfStringWithLink } from '../../../../lib/StringUtils/StringHTMLUtils';
import { useAppContext } from '../../../../contexts/AppContext/AppContext';
import { CreatePageChoice } from './partials/CreatePageChoice';
import { PageInformation, PageInformationParamType } from './partials/PageInformation';
import { IParameterProperty } from '../../../../types/IParameter';
import { TeamRoomAdmissionTypeOptions, TeamroomFormTemplate } from './partials/TeamroomFormTemplate';
import { EventFormTemplate } from './partials/EventFormTemplate';
import { ExternalLinkFormTemplate } from './partials/ExternalLinkFormTemplate';
import { TeaserInformationFormTemplate } from './partials/TeaserInformationFormTemplate';
import { ExternalContentFormTemplate } from './partials/ExternalContentFormTemplate';
import { PageType } from '../../../../types/api/PageType';
import { FetchState } from '../../../../hooks/reducers/useFetchReducer';
import {
  HiddenParam,
  RadioParam,
  SelectParam,
  SubmitButton,
  TextAreaParam,
  TextParam,
  UrlParam,
} from '../../../../_lib/Form2';
import { useFetch } from '../../../../hooks/useFetch';
import { formParamsUtils } from '../../../../_lib/Form2/utils/formParamsUtils';
import { AxiosError, AxiosResponse } from 'axios';
import { SonResponseError } from '../../../../_lib/Form2/types/SonResponseError';
import { ImageUploadParam } from '../../../../_lib/Form2/types/ImageUploadParam';
import { ImageFileUploadParam } from '../../../../_lib/Form2/types/FileUploadParam';
import { DateRangeParam } from '../../../../_lib/Form2/types/DateRangeParam';
import { FieldSet } from '../../../../_lib/Form2/types/FieldSet';
import usePrevious from '../../../../hooks/usePrevious';
import { getErrorMessagesForFormResponseErrors }
  from '../../../../components/molecules/Form/enums/FormResponseErrorType';

const TAB_PANEL_IDS = {
  PAGE_TYPE: 'multi-step-form-page-type',
  PAGE_INFORMATIONS: 'multi-step-form-page-informations',
  PAGE_PROPERTIES: 'multi-step-form-page-properties',
};

type CreatPageFormTemplateParamTypes = TextAreaParam<'teaserText'>
  | UrlParam<'url'>
  | UrlParam<'conferenceRoomUrl'>
  | TextParam<'name'>
  | TextAreaParam<'additionalInfo'>
  | TextParam<'location'>
  | HiddenParam<'teamroomOnlyRequested'>
  | SelectParam<'teamroomAdmissionType', TeamRoomAdmissionTypeOptions>
  | SelectParam<'readRestriction'>
  | DateRangeParam<'daterange'>
  | FieldSet<'pageTypes', RadioParam<'pageType'>>
  | ImageUploadParam<'imageuploadparams',
    TextParam<'alt'> | TextParam<'copyright'> | ImageFileUploadParam<'image'> | HiddenParam<'imageType'>>;

type CreatePageFormTemplateProps = {
  params: ReturnType<typeof formParamsUtils<CreatPageFormTemplateParamTypes>>;
  fetchState: FetchState<AxiosResponse<any>, AxiosError<SonResponseError>>;
};

const getTabFormTemplate = (
  params: ReturnType<typeof formParamsUtils<CreatPageFormTemplateParamTypes>>,
  selectedPageTypeValue: string) => {
  const url = params.findByName('url');
  const name = params.findByName('name');
  const teamroomAdmissionType = params.findByName('teamroomAdmissionType');
  const teamroomOnlyRequested = params.findByName('teamroomOnlyRequested');
  const imageUploadParams = params.findByName('imageuploadparams');
  const teaserText = params.findByName('teaserText');
  const additionalInfo = params.findByName('additionalInfo');
  const conferenceRoomUrl = params.findByName('conferenceRoomUrl');
  const location = params.findByName('location');
  const daterange = params.findByName('daterange');

  // External Application
  if (selectedPageTypeValue === PageType.externalapplication && url) {
    return <ExternalContentFormTemplate urlParam={url} />;
  }
  // Teamroom
  if (teamroomAdmissionType) {
    return (
      <TeamroomFormTemplate
        teamroomAdmissionTypeParam={teamroomAdmissionType}
        teamroomOnlyRequestedParam={teamroomOnlyRequested}
      />);
  }
  // External Link (Subpage of pagelist)
  if (url && name) {
    return <ExternalLinkFormTemplate urlParam={url} nameParam={name} />;
  }
  // Threads (Subpage of forum)
  if (imageUploadParams && teaserText) {
    return <TeaserInformationFormTemplate imageUploadParams={imageUploadParams} teaserTextParam={teaserText} />;
  }
  // Events (Subpage of eventlist)
  return (
    <EventFormTemplate
      dateRangeParam={daterange}
      additionalInfoParam={additionalInfo}
      locationParam={location}
      conferenceRoomUrlParam={conferenceRoomUrl}
    />
  );
};

export function CreatePageFormTemplate({
  params,
  fetchState,
}: CreatePageFormTemplateProps) {
  const { t } = useTranslation(Namespace.CreatePage);
  const { portalConfiguration } = useAppContext();
  const { get, requestError, isLoading } = useFetch();

  const [selectedPageTypeParameters, setSelectedPageTypeParameters] =
      useState<RadioParam<'pageType'> | undefined>(undefined);
  const previousSelectedPageTypeParameters = usePrevious(selectedPageTypeParameters);
  const [fetchedFormParameters, setFetchedFormParameters] = useState(
    formParamsUtils({ params: [] } as any));

  const fetchAdditionalFormParameters = useCallback(async () => {
    if (selectedPageTypeParameters && selectedPageTypeParameters.formParamsFetchUrl &&
      selectedPageTypeParameters.formParamsFetchUrl !== previousSelectedPageTypeParameters?.formParamsFetchUrl
    ) {
      const res = await get<Record<string, IParameterProperty[]>>(selectedPageTypeParameters.formParamsFetchUrl);

      if (res) {
        setFetchedFormParameters(formParamsUtils({
          params: Object.values(res.data).reduce((acc, curr) => {
            return acc.concat(curr);
          }, [] as any),
        } as any));
      }
    }
  }, [get, previousSelectedPageTypeParameters?.formParamsFetchUrl, selectedPageTypeParameters]);

  const renderSubmitAndLiveRegion = useMemo(() => {
    if (selectedPageTypeParameters?.value) {
      return (
        <SubmitButton
          buttonLabel={t(`${Namespace.PageManagement}:views.create-page-general.button`)}
          messageSuccess={
            t(
              `${Namespace.PageManagement}:views.create-page-general.status.success`,
              { page: selectedPageTypeParameters?.value },
            )
          }
          messageForward={`${Namespace.PageManagement}:views.create-page-general.status.successForward`}
          messageSending={t(
            `${Namespace.PageManagement}:views.create-page-general.status.sending`,
            { page: selectedPageTypeParameters?.value },
          )}
          messageFail={
            <>
              {t(
                `${Namespace.PageManagement}:views.create-page-general.status.fail`,
                { page: selectedPageTypeParameters?.value },
              )}
              {translatePartOfStringWithLink(
                `${Namespace.phrases}:ifProblemCallLinkedSupport`,
                portalConfiguration.son_portal_support_email
                  ? `mailto:${portalConfiguration.son_portal_support_email}`
                  : null,
              )}
            </>
          }
          customErrorTypeMessages={getErrorMessagesForFormResponseErrors()}
          fetchState={fetchState}
        />
      );
    }

    return null;
  }, [fetchState, portalConfiguration.son_portal_support_email, selectedPageTypeParameters?.value, t]);

  return (
    <MultiStepForm
      initialActiveTab={TAB_PANEL_IDS.PAGE_TYPE}
      fetchingData={{
        fetchFormParams: fetchAdditionalFormParameters,
        isLoading,
        requestError,
        loadingMessage: t(`${Namespace.phrases}:contentIsLoading`),
      }}
    >
      <Step id={TAB_PANEL_IDS.PAGE_TYPE} buttonLabel={t('steps.pageType.title')}>
        <CreatePageChoice
          params={(params.getByName('pageTypes') as FieldSet<'pageTypes', RadioParam<'pageType'>>)?.params}
          setSelectedPageTypeParameters={setSelectedPageTypeParameters}
        />
      </Step>
      <Step
        id={TAB_PANEL_IDS.PAGE_INFORMATIONS}
        buttonLabel={t('steps.pageInformation.title')}
        renderSubmitAndLiveRegion={selectedPageTypeParameters?.hasAdditionalTab ? null : renderSubmitAndLiveRegion}
        disabled={!selectedPageTypeParameters}
      >
        <PageInformation
          pageType={selectedPageTypeParameters?.value as PageType}
          params={fetchedFormParameters as PageInformationParamType}
        />
      </Step>
      {selectedPageTypeParameters?.hasAdditionalTab && (
        <Step
          id={TAB_PANEL_IDS.PAGE_PROPERTIES}
          buttonLabel={t('steps.pageProperties.title')}
          renderSubmitAndLiveRegion={renderSubmitAndLiveRegion}
          disabled={!selectedPageTypeParameters}
        >
          {getTabFormTemplate(
            fetchedFormParameters as ReturnType<typeof formParamsUtils<CreatPageFormTemplateParamTypes>>,
            selectedPageTypeParameters.value)}
        </Step>
      )}
    </MultiStepForm>
  );
}
